import type {CustomProperties, PropertyDefinition} from '@github-ui/custom-properties-types'
import {ListItem, ListView} from '@github-ui/list-view'
import {FilterIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, Box, Button, Text, TextInput, Token} from '@primer/react'
import {Blankslate} from '@primer/react/drafts'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useState} from 'react'

const propertyColumnResponsiveStyle: BetterSystemStyleObject = {
  width: [100, 100, 200],
}
interface Props {
  definitions: PropertyDefinition[]
  values: CustomProperties
  onEditClick?: () => void
}
export function RepoPropertiesList({definitions, values, onEditClick}: Props) {
  const [filterTerm, setFilterTerm] = useState('')

  if (definitions.length === 0) {
    return (
      <Blankslate border>
        <Blankslate.Heading>No custom properties set for this repository.</Blankslate.Heading>
      </Blankslate>
    )
  }

  const filteredDefinitions = definitions.filter(getFilterPredicate(filterTerm))

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <TextInput
        block
        value={filterTerm}
        onChange={e => setFilterTerm(e.target.value)}
        leadingVisual={FilterIcon}
        aria-label="Filter"
        placeholder="Filter properties"
      />

      <Box data-hpc sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}>
        {filteredDefinitions.length ? (
          <ListView
            title="Custom properties"
            variant="compact"
            metadata={
              <ListView.Metadata
                title={
                  <Text sx={{color: 'fg.muted'}}>
                    {filteredDefinitions.length} {filteredDefinitions.length === 1 ? 'property' : 'properties'}
                  </Text>
                }
                actionsLabel="Actions"
                actions={[
                  {
                    key: 'edit',
                    render(isOverflowMenu) {
                      if (!onEditClick) {
                        return <></>
                      }
                      return isOverflowMenu ? (
                        <ActionList.Item onClick={onEditClick}>
                          <ActionList.LeadingVisual>
                            <PencilIcon />
                          </ActionList.LeadingVisual>
                          Edit
                        </ActionList.Item>
                      ) : (
                        <Button leadingVisual={PencilIcon} onClick={onEditClick}>
                          Edit
                        </Button>
                      )
                    },
                  },
                ]}
              />
            }
          >
            {filteredDefinitions.map(definition => (
              <ListItem
                key={definition.propertyName}
                title={
                  <ListItem.Title
                    containerSx={{
                      ...propertyColumnResponsiveStyle,
                      px: 2,
                    }}
                    headerSx={{fontWeight: 600, wordWrap: 'break-word', whiteSpace: 'unset', display: 'block'}}
                    value={definition.propertyName}
                  />
                }
              >
                <ListItem.MainContent>
                  <Box sx={{height: '100%', pt: 2, maxWidth: '100%', wordWrap: 'break-word'}}>
                    <ValueCell definition={definition} value={values[definition.propertyName]} />
                  </Box>
                </ListItem.MainContent>
              </ListItem>
            ))}
          </ListView>
        ) : (
          <Blankslate border>
            <Blankslate.Heading>No properties that match</Blankslate.Heading>
          </Blankslate>
        )}
      </Box>
    </Box>
  )
}

function ValueCell({definition, value}: {definition: PropertyDefinition; value?: string}) {
  const {valueType} = definition
  const displayValue = value || definition.defaultValue
  if (!displayValue) {
    return <Text sx={{color: 'fg.muted'}}>No value set</Text>
  }

  switch (valueType) {
    case 'string':
      return <>{displayValue}</>
    case 'single_select':
    case 'boolean':
      return <Token data-testid="single-select-cell" text={displayValue} />
  }
}

function getFilterPredicate(term: string) {
  return (item: PropertyDefinition) => item.propertyName.toLowerCase().includes(term.toLowerCase())
}

try{ RepoPropertiesList.displayName ||= 'RepoPropertiesList' } catch {}
try{ ValueCell.displayName ||= 'ValueCell' } catch {}